/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global setTimeout, console, document, Excel, Office, clearTimeout, localStorage, window, setInterval, clearInterval, $ */

import { TreeSortable } from "./js/treeSortable.js";
import "./taskpane.css";
import "./css/treeSortable.css";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

// Then import jquery-ui (or load it via a script tag that comes later)
import "jquery-ui/ui/widgets/sortable";

import { library, dom } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
//import { far } from "@fortawesome/free-regular-svg-icons";
//import { fab } from "@fortawesome/free-brands-svg-icons";

library.add(fas);

const settings = {
  sanityChecks: {
    label: "Sanity Checks",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("sanityChecks") === "true",
  },
  showHiddenIfHasChildren: {
    label: "Show Hidden If Has Children",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("showHiddenIfHasChildren") === "true",
  },
  showHiddenAlways: {
    label: "Show Hidden Always",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("showHiddenAlways") === "true",
  },
  colorfulTabs: {
    label: "Colorful Tabs",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("colorfulTabs") === "true",
  },
  hideParentHidesBranch: {
    label: "hideParentHidesBranch",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("hideParentHidesBranch") === "true",
  },
  inheritColor: {
    label: "inheritColor",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("inheritColor") === "true",
  },
  showTooltips: {
    label: "showTooltips",
    description: "",
    type: "checkbox",
    value: localStorage.getItem("showTooltips") === "true",
  },
  newWorksheetType: {
    label: "Create new worksheets...",
    description: "",
    type: "radio",
    options: {
      type1: "...as child of current",
      type2: "...as sibling of current",
      type3: "...at the end",
    },
    value: localStorage.getItem("newWorksheetType") || "type1",
  },
};

export const CONFIG = {
  metadataKey: { value: "TreeSheetsMeta" },
  debugKey: { value: "d" },
  defaultTabColor: { value: "" },
  ...settings,
};

console.log("CONFIG:", CONFIG);

let interval;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class PriorityTaskQueue2 {
  constructor() {
    this.queue = Promise.resolve();
    this.pendingTasks = [];
    this.currentTaskComment = null; // Stores the comment of the active task
    this.currentTaskId = null; // Stores the unique task ID

    // Intercept console.log
    this.originalConsoleLog = console.log;
    console.log = this.interceptedLog.bind(this);
  }

  interceptedLog(...args) {
    if (this.currentTaskId) {
      this.originalConsoleLog(`[${this.currentTaskId}]`, ...args);
    } else {
      this.originalConsoleLog(...args);
    }
  }

  add(task, comment = "", priority = false) {
    return new Promise((resolve) => {
      const taskId = Math.floor(10000 + Math.random() * 90000); // Generate a 5-digit task ID

      const wrappedTask = async (prevResult) => {
        this.currentTaskComment = comment || "Unnamed Task";
        this.currentTaskId = taskId; // Assign the task ID

        console.log(`${"".padEnd(135, ">")}`);
        console.log(`TASK START "${this.currentTaskComment}"`);

        const result = await task(prevResult); // Run the task

        console.log(`TASK END "${this.currentTaskComment}"`);
        console.log(`${"".padEnd(135, "<")}`);

        this.currentTaskComment = null;
        this.currentTaskId = null; // Reset task ID after task finishes

        resolve(result);
        return result;
      };

      const taskEntry = { task: wrappedTask, comment };

      if (priority) {
        this.pendingTasks.unshift(taskEntry);
      } else {
        this.pendingTasks.push(taskEntry);
      }

      if (this.pendingTasks.length === 1) {
        this.processNext();
      }
    });
  }

  processNext(prevResult) {
    if (this.pendingTasks.length === 0) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { task, comment } = this.pendingTasks.shift();

    this.queue = this.queue.then(() => task(prevResult)).then((result) => this.processNext(result));
  }
}

export class PriorityTaskQueue {
  constructor() {
    this.queue = Promise.resolve();
    this.pendingTasks = [];
  }

  add(task, comment = "", priority = false) {
    return new Promise((resolve) => {
      const taskId = `task-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

      const wrappedTask = async (prevResult) => {
        console.log(`${"".padEnd(135, ">")}`);
        console.log(`TASK START >>> ${comment || taskId}`);
        const result = await task(prevResult); // Pass previous task's result
        console.log(`TASK END <<< ${comment || taskId}`);
        console.log(`${"".padEnd(135, "<")}`);
        resolve(result);
        return result; // Pass result to the next task
      };

      const taskEntry = { task: wrappedTask, comment };

      if (priority) {
        this.pendingTasks.unshift(taskEntry); // Insert at the front
      } else {
        this.pendingTasks.push(taskEntry); // Insert at the back
      }

      if (this.pendingTasks.length === 1) {
        this.processNext();
      }
    });
  }

  processNext(prevResult) {
    if (this.pendingTasks.length === 0) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { task, comment } = this.pendingTasks.shift();

    this.queue = this.queue
      .then(() => task(prevResult)) // Pass previous result
      .then((result) => this.processNext(result)); // Pass result to the next task
  }
}

export const taskQueue = new PriorityTaskQueue();

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Excel) {
    console.log(settings);
    for (let key in settings) {
      const setting = settings[key];
      if (setting.type === "checkbox") {
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.id = key;
        checkbox.checked = setting.value;

        const label = document.createElement("label");
        label.htmlFor = key;
        label.textContent = `${setting.label} (${setting.description})`;

        const div = document.createElement("div");
        div.appendChild(checkbox);
        div.appendChild(label);

        document.getElementById("settings").appendChild(div);

        checkbox.addEventListener("change", (event) => {
          localStorage.setItem(key, event.target.checked);
          setting.value = event.target.checked;
        });
      } else if (setting.type === "radio") {
        const radioDiv = document.createElement("div");

        const label = document.createElement("label");
        label.textContent = `${setting.label} (${setting.description})`;
        radioDiv.appendChild(label);
        radioDiv.appendChild(document.createElement("br"));

        Object.entries(setting.options).forEach(([option, optionLabel]) => {
          const radio = document.createElement("input");
          radio.type = "radio";
          radio.id = option;
          radio.name = key;
          radio.value = option;
          radio.checked = setting.value === option;

          const radioLabel = document.createElement("label");
          radioLabel.htmlFor = option;
          radioLabel.textContent = optionLabel;

          radioDiv.appendChild(radio);
          radioDiv.appendChild(radioLabel);
          radioDiv.appendChild(document.createElement("br"));

          radio.addEventListener("change", (event) => {
            if (event.target.checked) {
              localStorage.setItem(key, event.target.value);
              setting.value = event.target.value;
            }
          });
        });
        document.getElementById("settings").appendChild(radioDiv);
      }
    }

    /*
    if (localStorage.getItem("defaultView") == "tree") {
      $("#hierarchyButton").addClass("active");
      $("#flatButton").removeClass("active");
    } else {
      $("#hierarchyButton").removeClass("active");
      $("#flatButton").addClass("active");
    }
    */

    const theme = Office.context.officeTheme;
    if (theme) {
      const backgroundColor = theme.bodyBackgroundColor;
      const foregroundColor = theme.bodyForegroundColor;
      document.body.style.backgroundColor = backgroundColor;
      document.body.style.color = foregroundColor;
    }

    taskQueue.add(async () => {
      initializeSheetHandlers();
      initializeDebugInfoHandler();
      initializeOtherHandlers();
    }, "Invoke sheet handler functions");

    window.addEventListener(
      "wheel",
      debounce(function (e) {
        if (e.ctrlKey) {
          localStorage.setItem("TreeSheetsZoom", document.body.style.zoom * window.devicePixelRatio);
        }
      }, 100)
    );

    window.addEventListener("keydown", function (e) {
      if (e.ctrlKey && (e.key === "+" || e.key === "-")) {
        setTimeout(function () {
          localStorage.setItem("TreeSheetsZoom", document.body.style.zoom * window.devicePixelRatio);
        }, 100);
      } else if (e.ctrlKey && e.key === "0") {
        localStorage.setItem("TreeSheetsZoom", 1);
        document.body.style.zoom = 1;
      }
    });

    document.body.style.zoom = parseFloat(localStorage.getItem("TreeSheetsZoom"));

    document.getElementById("modalBtn1").addEventListener("click", () => resolveMessage(true));
    document.getElementById("modalBtn2").addEventListener("click", () => resolveMessage(false));

    //setTimeout(trackLoop, 2000);

    taskQueue.add(async () => {
      const twp = await trackWorksheetPositions("get");
      console.log("trackWorksheetPositions('get') result:", twp);
    }, "initial trackWorksheetPositions('get')");

    taskQueue.add(async () => {
      await loadSheets();
    }, "loadSheets in Office.onReady");
  }

  document.getElementById("getWorkMeta").addEventListener("click", async function () {
    document.getElementById("metaInput").value = beautifyJson(JSON.stringify(await getWorkbookMeta()));
  });

  document.getElementById("saveWorkMeta").addEventListener("click", async function () {
    await saveWorkbookMeta(JSON.parse(document.getElementById("metaInput").value));
  });
});

function beautifyJson(json) {
  try {
    const parsed = JSON.parse(json);
    return JSON.stringify(parsed, null, 2); // 2 spaces for indentation
  } catch (error) {
    return "Invalid JSON";
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function trackLoop() {
  taskQueue.add(async () => {
    console.log(await trackWorksheetPositions("track"));
  }, "trackWorksheetPositions('track')");
  setTimeout(trackLoop, 2000);
}

function initializeOtherHandlers() {
  document.getElementById("settingsButton").addEventListener("click", function () {
    if ($("#settingsButton").hasClass("active")) return;
    $("#tree").hide();
    $("#settingsButton").addClass("active");
    $("#treeButton").removeClass("active");
    $("#settings").show();
  });

  document.getElementById("treeButton").addEventListener("click", function () {
    if ($("#treeButton").hasClass("active")) return;
    $("#settings").hide();
    $("#settingsButton").removeClass("active");
    $("#treeButton").addClass("active");
    $("#tree").show();
    taskQueue.add(async () => {
      await loadSheets();
    }, "loadSheets after leaving tree view");
  });
}

async function setTabColor(tabColor) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.tabColor = tabColor;
      await context.sync();
    });
    console.log(`Tab Color Set: ${tabColor}`);
  } catch (error) {
    console.error(`Error setting tab color: ${error}`);
  }
}

async function getTabColorAndUpdateTreeColor() {
  console.log("getTabColorAndUpdate");
  try {
    const sheetData = await getActiveSheet();

    $(".tree-branch.active .branch-wrapper").css("background-color", sheetData.tabColor);

    const color = data.find((item) => item.id === sheetData.id).tabColor;
    if (color != sheetData.tabColor) {
      await trackWorksheetPositions("save");
    }

    // Calculate luminance to decide if text should be inverted
    const getLuminance = (tabColor) => {
      const hex = tabColor.replace(/^#/, "");
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return (r * 0.299 + g * 0.587 + b * 0.114) / 255;
    };

    const defaultColor = CONFIG.defaultTabColor.value;
    const invert = getLuminance(sheetData.tabColor || defaultColor) <= 0.5;
    $("li.active").toggleClass("invert", invert);
    /*
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      let sheets = context.workbook.worksheets;
      sheets.load("items.id, items.tabColor, items.visibility, items.name, items.position");
      await context.sync();
      console.log(sheets);
    });
    */
  } catch (error) {
    console.error("Error retrieving tab color:", error);
  }
}

function initializeDebugInfoHandler() {
  console.log("initializeDebugInfoHandler");
  let keyPressCount = 0;
  let resetTimer;
  const targetKey = CONFIG.debugKey.value.toLowerCase();

  document.body.addEventListener("keydown", ({ key }) => {
    if (key.toLowerCase() !== targetKey) return;

    keyPressCount++;
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => (keyPressCount = 0), 500);

    if (keyPressCount === 3) {
      const debugContainer = document.getElementById("debug-meta-container");
      const isActive = debugContainer.style.display !== "block";
      debugContainer.style.display = isActive ? "block" : "none";
      document.body.dataset.debugActive = isActive;
      keyPressCount = 0;
    }
  });
}

let debug = true;
export let data = [];

let Sortable;
let globalTippy;

export async function loadSheets(sheetId = null) {
  console.log("loadSheets", sheetId);
  sheetId = undefined;

  let data = getWorkbookMeta();
  console.log("getWorkbookMeta result:", data);
  const TreeId = "#tree";
  const $Tree = $(TreeId);
  Sortable = new TreeSortable({
    treeSelector: TreeId,
  });

  window.Sortable = Sortable;

  console.log("data sort");
  data.sort((a, b) => a.position - b.position);
  
  let res = { data: data };
  if (CONFIG.sanityChecks.value) {
    res = await sanityCheck(data);
  }
  console.log("resres", res);

  if (res.result == true) {
    const dataMap = new Map(data.map((entry) => [entry.id, entry]));
    const getDescendants = (parentId) =>
      data.filter((entry) => entry.parent_id === parentId).flatMap((entry) => [entry, ...getDescendants(entry.id)]);
    res.data.forEach(({ id }) => {
      const entry = dataMap.get(id);
      if (entry) {
        const diff = entry.level - 1;
        entry.level = 1;
        entry.parent_id = "0";
        getDescendants(id).forEach((child) => (child.level -= diff));
      }
    });
    trackWorksheetPositions("save");
  }

  const $content = data.map((item) => Sortable.createBranch(item, data, 1));
  $Tree.html($content);


  //TODO add read-only logic
  let settings;
  await Excel.run(async (context) => {
    settings = context.workbook.load("readonly, isDirty, settings, protection");
    await context.sync();
  });

  console.log("settings readOnly", settings.readOnly);
  console.log("settings protected", settings.protection.protected);

  const isDisabled = settings.readOnly || settings.protection.protected ? true : false;

  if (isDisabled) {
    $("#tree").addClass("disabled");
  } else {
    $("#tree").removeClass("disabled");
  }

  Sortable.run(isDisabled);

  /*const delay = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  };
  */
  $(document).off();

  Sortable.addListener("click", ".tree-branch", function (event) {
    taskQueue.add(async () => {
      console.log(event);
      event.preventDefault();
      if (
        $(event.target).closest("button").hasClass("remove-branch") ||
        $(event.target).closest("button").hasClass("add-sibling") ||
        $(event.target).closest("button").hasClass("add-child")
      ) {
        console.log("Clicked a button, returning");
        return;
      }
      if (!$(event.currentTarget).hasClass("locked")) {
        //console.log("add to activatedByTreeSheets", $(event.currentTarget).attr("data-id"));
        //activatedByTreeSheets.add($(event.currentTarget).attr("data-id"));
        //setTreeTabActive($(event.currentTarget).attr("data-id"));
        //await sheetActivatedHandler($(event.currentTarget).attr("data-id"));

        console.log("activateSheet 1");
        setTreeTabActive($(event.currentTarget).attr("data-id"));
        await activateSheet($(event.currentTarget).attr("data-id"));
      }
    }, "Clicked a tab in the tree, activating corresponding worksheet.");
  });

  Sortable.onSortCompleted(async (ui, a, b, c, d, e) => {
    
    //FIXME cant add everything to the movedByTreeSheets set...
    //sortedByTreeSheets.add(ui.item.attr("data-id"));
    taskQueue.add(async () => {
      //c = item, d = descendants
      console.log(ui, a, b, c, d, e);

      //const elements = b.add(c);
      const elements = $("#tree li");
      let objectArray = [];

      elements.each(function () {
        const id        = $(this).attr("data-id");
        const position  = $(this).attr("data-position");
        const parent_id = $(this).attr("data-parent");
        objectArray.push({ id, position, parent_id });
      });

      determineMovedEvents(getWorkbookMeta(), objectArray).forEach((id) => {
        console.log("add to movedByTreeSheets", id);
        movedByTreeSheets.add(id);
      });

      if (e) {
        objectArray = sortByParent(objectArray);
        await applyWorksheetPositions(objectArray, null, true);
        await trackWorksheetPositions("save", b, c, true);
        return;
      }

      await applyWorksheetPositions(objectArray, null, true);
      await trackWorksheetPositions("save", b, c, d);
    }, "onSortCompleted");
  });

  if (!isDisabled) {
    Sortable.addListener("click", ".add-child", function (event, instance) {
      taskQueue.add(async () => {
        console.log(".add-child", event, instance);
        event.preventDefault();
        await instance.addChildBranch($(event.target)).then(async (newId) => {
          //setTreeTabActive(newId);
          console.log("newId", newId);
          console.log("added to addedByTreeSheets", newId);
          //console.log("added to activatedByTreeSheets", newId);
          addedByTreeSheets.add(newId);
          if ($("[data-id='" + newId + "']").next("li").length) {
            console.log("added to movedByTreeSheets", newId);
            movedByTreeSheets.add(newId);
          }
          //activatedByTreeSheets.add(newId);
          //await sheetActivatedHandler(newId);
          //await updateDataAttributes($("[data-id='" + newId + "']").index());
          await trackWorksheetPositions("save", 1);
        });
        dom.i2svg();
        globalTippy.forEach((instance) => instance.destroy());
        globalTippy = tippy("[data-tippy-content]");
      }, "Clicked add child branch.");
    });

    Sortable.addListener("click", ".add-sibling", function (event, instance) {
      taskQueue.add(async () => {
        event.preventDefault();
        console.log(instance);
        await instance.addSiblingBranch($(event.target)).then(async (newId) => {
          //setTreeTabActive(newId)
          console.log("newId", newId);
          console.log("added to addedByTreeSheets", newId);
          //console.log("added to activatedByTreeSheets", newId);
          addedByTreeSheets.add(newId);
          if ($("[data-id='" + newId + "']").next("li").length) {
            console.log("added to movedByTreeSheets", newId);
            movedByTreeSheets.add(newId);
          }
          //activatedByTreeSheets.add(newId);
          //await sheetActivatedHandler(worksheetId);
          //await updateDataAttributes($("[data-id='" + newId + "']").index());
          await trackWorksheetPositions("save", 2);
        });
        dom.i2svg();
        globalTippy.forEach((instance) => instance.destroy());
        globalTippy = tippy("[data-tippy-content]");
      }, "Clicked add sibling branch.");
    });

    Sortable.addListener("click", ".remove-branch", async function (event, instance) {
      taskQueue.add(async () => {
        event.preventDefault();
        await instance.removeBranch($(event.target));
        //trackWorksheetPositions("save", 3);
      }, "Clicked remove branch.");
    });
  }

  taskQueue.add(async () => {
    const activeSheet = await getActiveSheet();
    setTreeTabActive(activeSheet.id);
  }, "Sheets reloaded... Setting the active sheet in the tree.");

  clearInterval(interval);
  
  if (!isDisabled) {
    if (CONFIG.colorfulTabs.value) {
      interval = setInterval(() => {
        taskQueue.add(async () => {
          await getTabColorAndUpdateTreeColor();
        }, "colorfulTabs");
      }, 1000);
    } else {
      clearInterval(interval);
    }
  }

  /*
  taskQueue.add(async () => {
    await updateDataAttributes(0);
  }, "Updating data attributes for all sheets.");
  */

  dom.i2svg();
  globalTippy = tippy("[data-tippy-content]");
}

function determineMovedEvents(oldState, newState) {
  // Build lookup maps for each object's index based on the array order.
  const oldIndex = {};
  oldState.forEach((obj, idx) => { 
    oldIndex[obj.id] = idx; 
  });
  
  const newIndex = {};
  newState.forEach((obj, idx) => { 
    newIndex[obj.id] = idx; 
  });
  
  // Compute the positional change (diff) for each object.
  // If an object moved to an earlier index, its diff will be negative.
  const negativeGroup = [];
  const positiveGroup = [];
  
  oldState.forEach(obj => {
    const diff = newIndex[obj.id] - oldIndex[obj.id];
    if (diff < 0) {
      negativeGroup.push(obj.id);
    } else if (diff > 0) {
      positiveGroup.push(obj.id);
    }
  });
  
  // Our heuristic: if any object moved upward (negative diff), return those.
  // Otherwise, return the ones that moved downward.
  if (negativeGroup.length > 0) {
    return negativeGroup;
  } else {
    return positiveGroup;
  }
}

function sortByParent(items) {
  const map = new Map();
  items.forEach((item) => {
    if (!map.has(item.parent_id)) {
      map.set(item.parent_id, []);
    }
    map.get(item.parent_id).push(item);
  });

  function buildTree(parentId, position = { value: 0 }) {
    return (map.get(parentId) || [])
      .sort((a, b) => a.position - b.position)
      .flatMap((item) => {
        item.position = position.value++;
        return [item, ...buildTree(item.id, position)];
      });
  }

  return buildTree("0");
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function sanityCheck(data) {
  console.log("sanityCheck", data);
  return new Promise((resolve, reject) => {
    try {
      const ids = new Set(data.map((item) => item.id));
      const invalidParents = data.filter((item) => item.parent_id !== "0" && !ids.has(item.parent_id));

      // Get the name attributes and join them with a comma
      const names = invalidParents.map((item) => item.name).join(", ");

      console.log("Invalid parents:", names);
      if (invalidParents.length > 0) {
        console.warn("Invalid parent references found:", invalidParents);
        // eslint-disable-next-line no-undef
        /*openDialog(
          "One or more of your sheets have an invalid parent reference. The affected sheet(s) will be promoted to the top.",
          "OK",
          "Cancel"
        )
          .then((dialogResult) => {
            resolve({ result: dialogResult, data: invalidParents });
          })
          .catch(reject);*/

        showMessage(
          "One or more of your sheets have an invalid parent reference. The affected sheet(s) will be promoted to the top.<br><br>Affected sheets: " +
            names,
          "OK",
          "Cancel"
        )
          .then((dialogResult) => {
            resolve({ result: dialogResult, data: invalidParents });
          })
          .catch(reject);

        return;
      }

      resolve({ result: "OK", data: [] });
    } catch (error) {
      reject(error);
    }
  });
}

async function getActiveSheet() {
  console.log("getActiveSheet");
  return Excel.run(async (context) => {
    const activeSheet = context.workbook.worksheets.getActiveWorksheet();
    activeSheet.load("name, id, tabColor, position");
    await context.sync();
    console.log("Active sheet is", activeSheet.name, activeSheet.id);
    return {
      id: activeSheet.id,
      name: activeSheet.name,
      tabColor: activeSheet.tabColor,
      position: activeSheet.position,
    };
  }).catch((error) => {
    console.error(error);
  });
}

async function getSheet(id) {
  console.log("getSheet");
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(id);
    sheet.load("name, id, tabColor, position");
    await context.sync();
    console.log("sheet data:", sheet.name, sheet.id);
    return {
      id: sheet.id,
      name: sheet.name,
      tabColor: sheet.tabColor,
      position: sheet.position,
    };
  }).catch((error) => {
    console.error(error);
  });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function saveWorkbookMeta(metadata) {
  console.log("saveWorkbookMeta", metadata);
  return new Promise((resolve, reject) => {
    try {

      const metadataJson = JSON.stringify(metadata);
      Office.context.document.settings.set(CONFIG.metadataKey.value, metadataJson);
      Office.context.document.settings.saveAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          console.log("Workbook metadata saved successfully.");
          resolve();
        } else {
          console.error("Failed to save workbook metadata:", asyncResult.error.message);
          reject(asyncResult.error.message);
        }
      });
    } catch (error) {
      console.error("Error saving workbook metadata:", error);
      reject(error);
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getWorkbookMeta() {
  console.log("getWorkbookMeta");
  try {
    const metadataJson = Office.context.document.settings.get(CONFIG.metadataKey.value);
    const metadata = metadataJson != null ? JSON.parse(metadataJson) : null;
    if (metadata) metadata.sort((a, b) => a.position - b.position); //TODO is this needed?
    return metadata;
  } catch (error) {
    console.error("Error retrieving workbook metadata:", error);
    return null;
  }
}

function initializeSheetHandlers() {
  console.log("initializeSheetHandlers");
  Excel.run(function (context) {
    const workbook = context.workbook;
    workbook.worksheets.onAdded.add(sheetAddedHandler);
    workbook.worksheets.onDeleted.add(sheetDeletedHandler);
    workbook.worksheets.onNameChanged.add(sheetNameChangedHandler);
    workbook.worksheets.onMoved.add(sheetMovedHandler);
    workbook.worksheets.onActivated.add(sheetActivatedHandler);
    workbook.worksheets.onVisibilityChanged.add(sheetVisibilityHandler);
    return context.sync();
  }).catch(function (error) {
    console.error(error);
  });
}

function debounce(fn, delay = 1000, timers = new Map()) {
  return (callback, ...params) => {
    // Clear any previously scheduled call for this callback
    clearTimeout(timers.get(callback));
    // Schedule a new call passing the callback and any additional parameters
    timers.set(
      callback,
      setTimeout(() => fn(callback, ...params), delay)
    );
  };
}

async function applyWorksheetPositions(worksheetObjects, previousActive, tree) {
  console.log("applyWorksheetPositions", worksheetObjects, previousActive, tree);
  if (tree) {
    try {
      return Excel.run(async (context) => {
        const workbook = context.workbook;

        let tempArr = [];

        for (const obj of worksheetObjects) {
          console.log(obj.name, obj.position, obj.id);
          const domElement = document.querySelector(`[data-id="${obj.id}"]`);
          if (domElement && (domElement.classList.contains("locked") || domElement.classList.contains("hidden"))) {
            visibilityByTreeSheets.add(obj.id);
            tempArr.push(obj.id);
          }
          const sheet = workbook.worksheets.getItem(obj.id);
          sheet.visibility = "Visible";
          sheet.position = parseInt(obj.position);
        }

        await context.sync();

        if (tempArr.length > 0) {
          for (const obj of tempArr) {
            const sheet = workbook.worksheets.getItem(obj);
            sheet.visibility = "Hidden";
          }
          await context.sync();
        }

        if (previousActive) {
          //console.log("add to activatedByTreeSheets", previousActive);
          //activatedByTreeSheets.add(previousActive);
          console.log("activateSheet 2", activateSheet);
          await activateSheet(previousActive);
        }
        console.log("worksheets moved");
      });
    } catch (error) {
      console.error("Error moving worksheets:", error);
    }
  } else {
    console.log("else");
  }
}

async function moveWorksheetToPosition(worksheetId, previousActive, position) {
  console.log("moveWorksheetToPosition", worksheetId, previousActive, position);
  try {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(worksheetId);
      sheet.position = parseInt(position);
      await context.sync();
      if (previousActive != null) {
        //console.log("added to activatedByTreeSheets", previousActive);
        //activatedByTreeSheets.add(previousActive);
        console.log("activateSheet 3", activateSheet);
        await activateSheet(previousActive);
      }
      console.log("worksheet moved");
    });
  } catch (error) {
    console.error("Error moving worksheet:", error);
  }
}

// Simplified getSheetDetails: only returns the necessary properties.
function getSheetDetails(id) {
  // Find the sheet by id.
  const sheet = data.find((item) => item.id === id);
  if (!sheet) return null;

  // Get direct children sorted by position.
  const directChildren = data.filter((item) => item.parent_id === id).sort((a, b) => a.position - b.position);
  const lastChild = directChildren.length ? directChildren[directChildren.length - 1] : null;

  // Get siblings (items with the same parent) sorted by position.
  const siblings = data.filter((item) => item.parent_id === sheet.parent_id).sort((a, b) => a.position - b.position);
  const lastSibling = siblings.length ? siblings[siblings.length - 1] : null;

  // Helper: Recursively get the deepest descendant from a given node.
  function getDeepestLastDescendant(node) {
    let current = node;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const children = data.filter((item) => item.parent_id === current.id).sort((a, b) => a.position - b.position);
      if (!children.length) break;
      current = children[children.length - 1];
    }
    return current;
  }

  const lastDescendantOfLastChild = lastChild ? getDeepestLastDescendant(lastChild) : null;
  const lastDescendantOfLastSibling = lastSibling ? getDeepestLastDescendant(lastSibling) : null;

  return {
    attributes: sheet,
    lastChild,
    lastSibling,
    lastDescendantOfLastChild,
    lastDescendantOfLastSibling,
  };
}

async function sheetAddedHandler(event) {
  taskQueue.add(async () => {
    console.log("sheetAddedHandler", event);
    if (addedByTreeSheets.has(event.worksheetId)) {
      console.log("delete from addedByTreeSheets", event.worksheetId);
      addedByTreeSheets.delete(event.worksheetId);
      console.log("addedByTreeSheets event ignored");
    } else {
      const sheet = await getSheet(event.worksheetId);
      const posOriginator = sheet.position;
      const name = sheet.name;
      const workbookmeta = getWorkbookMeta();

      const dataOriginator = workbookmeta.find((item) => item.position === posOriginator - 1);

      const deets = getSheetDetails(dataOriginator.id);
      console.log("deets: ", deets);

      /*
      type1: "...as child of current",
      type2: "...as sibling of current",
      type3: "...at the end"
      */

      let obbj = {
        id: event.worksheetId,
        name: name,
        position: "",
        parent_id: deets.attributes.parent_id,
        level: parseInt(deets.attributes.level) + (CONFIG.newWorksheetType.value === "type1" ? 1 : 0),
        tabColor: CONFIG.colorfulTabs.value ? deets.attributes.tabColor : "",
        visibility: "Visible",
      };

      if (CONFIG.newWorksheetType.value === "type1" || CONFIG.newWorksheetType.value === "type2") {
        const pos =
          CONFIG.newWorksheetType.value === "type1"
            ? (deets.lastDescendantOfLastChild?.position ?? posOriginator - 1)
            : CONFIG.newWorksheetType.value === "type2"
              ? (deets.lastDescendantOfLastSibling?.position ?? deets.lastSibling?.position ?? posOriginator)
              : posOriginator;

        console.log(pos);

        obbj = {
          ...obbj,
          position: pos + 1,
        };

        $("#tree li").eq(pos).after(Sortable.createBranch(obbj, data));

        dom.i2svg();
        globalTippy.forEach((instance) => instance.destroy());
        globalTippy = tippy("[data-tippy-content]");

        await getWorkbookMeta();

        $("#tree").calculateSiblingDistances();
        Sortable.updateBranchZIndex();
        Sortable.branchesLeftShifting();

        //await saveWorkbookMeta(); //shouldnt be here me thinks
        if (CONFIG.newWorksheetType.value === "type1") {
          setTabColor(obbj.tabColor);
        }
      } else if (CONFIG.newWorksheetType.value === "type3") {
        console.log("moveWorksheetToPosition 1");
        $("#tree li")
          .eq(data.length - 1)
          .after(Sortable.createBranch({ ...obbj, position: data.length, level: "1" }, data));
    
        dom.i2svg();
        globalTippy.forEach((instance) => instance.destroy());
        globalTippy = tippy("[data-tippy-content]");

        $("#tree").calculateSiblingDistances();
        Sortable.updateBranchZIndex();
        Sortable.branchesLeftShifting();
        await moveWorksheetToPosition(event.worksheetId, null, data.length);
      }
      await trackWorksheetPositions("save");
    }
  }, "sheetAddedHandler");
}

function sheetDeletedHandler(event) {
  taskQueue.add(async () => {
    console.log("sheetDeletedHandler", event);
    if (deletedByTreeSheets.has(event.worksheetId)) {
      // If deletion is triggered by code, remove it from the set and ignore the event
      console.log("delete from deletedByTreeSheets", event.worksheetId);
      deletedByTreeSheets.delete(event.worksheetId);
      console.log("sheetDeletedHandler event ignored");
      return;
    } else {
      //console.log("calling loadSheets 5");
      Sortable.removeBranch($("[data-id='" + event.worksheetId + "']"), true);
      /*
      taskQueue.add(async () => {
        await loadSheets();
        console.log("done");
      });
      */
      //await trackWorksheetPositions("save");
    }
  }, "Sheet removed");
}

async function sheetMovedHandler(ev) {
  //TODO prevent moving of invisble sheets !!!
  taskQueue.add(async () => {
    console.log("sheetMovedHandler", ev);
    if (movedByTreeSheets.has(ev.worksheetId)) {
      console.log("delete from deletedByTreeSheets", ev.worksheetId);
      movedByTreeSheets.delete(ev.worksheetId);
      console.log("sheetMovedHandler event ignored");
      return;
    }
    await trackWorksheetPositions(
      "save",
      Math.min(ev.positionBefore, ev.positionAfter),
      Math.abs(ev.positionBefore - ev.positionAfter)
    );
  }, "sheetMovedHandler");
}

function sheetNameChangedHandler(event) {
  $("li[data-id='" + event.worksheetId + "'] .branch-title").text(event.nameAfter);
}

async function sheetVisibilityHandler(ev) {
  taskQueue.add(async () => {
    console.log("sheetVisibilityHandler", ev);
    if (visibilityByTreeSheets.has(ev.worksheetId)) {
      console.log("delete from visibilityByTreeSheets", ev.worksheetId);
      visibilityByTreeSheets.delete(ev.worksheetId);
      console.log("sheetVisibilityHandler event ignored");
      return;
    } else {


      await trackWorksheetPositions("save");

      const sheetData = { ...data.find((item) => item.id === ev.worksheetId), visibility: ev.visibilityAfter };
      $("li[data-id='" + ev.worksheetId + "']").replaceWith(Sortable.createBranch(sheetData, getWorkbookMeta()));

      dom.i2svg();
      globalTippy.forEach((instance) => instance.destroy());
      globalTippy = tippy("[data-tippy-content]");

      $("#tree").calculateSiblingDistances();
      Sortable.updateBranchZIndex();
      Sortable.branchesLeftShifting();

      //await loadSheets(); //FIXME
    }
  }, "sheetVisibilityHandler");
}

function setTreeTabActive(worksheetId) {
  console.log("setTreeTabActive", worksheetId);
  //WORKAROUND loadSheets to circumvent race condition when quick tapping add sheets in the bottom bar
  if ($("[data-id='" + worksheetId + "']").length === 0) {
    taskQueue.add(async () => {
      await loadSheets();
    }, "problem task?");
  }
  if ($("li.active").attr("data-id") != worksheetId) {
    $("li.active").first().removeClass("active");
    $("[data-id='" + worksheetId + "']")
      .first()
      .addClass("active");
  }
}

async function activateSheet(id) {
  console.log("activateSheet", id);
  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(id);
    sheet.activate();
    await context.sync();
  });
}

async function sheetActivatedHandler(ev) {
  taskQueue.add(async () => {
    console.log("sheetActivatedHandler", ev);
    await setTreeTabActive(ev.worksheetId);
    /*
    if (activatedByTreeSheets.has(ev.worksheetId)) {
      console.log("delete from activatedByTreeSheets", ev.worksheetId);
      activatedByTreeSheets.delete(ev.worksheetId);
      /*
      await Excel.run(async (context) => {
        console.log(`Worksheet ${ev.worksheetId} activated`);
          if (debug) {
            console.log("getSheetmeta for debug");
            await getSheetMeta(id).then((meta) => {
              if (meta) {
                document.getElementById("metaInput").value = JSON.stringify(meta);
                //console.log(data);
              }
            });
          }
      }).catch(function (error) {
        console.error(error);
      });
    } else {
      console.log("sheetActivatedHandler event ignored");
    }
    */
  }, "sheetActivatedHandler");
}

/*
async function sheetActivatedHandler2(event) {
  taskQueue.add(async () => {
    console.log("sheetActivatedHandler", event);

    setTreeTabActive(event.worksheetId);

    if (activatedByTreeSheets.has(event.worksheetId)) {
      activatedByTreeSheets.delete(event.worksheetId);
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(event.worksheetId);
        //sheet.position = 1;
        sheet.activate();
        await context.sync();
        console.log(`Worksheet ${event.worksheetId} activated`);
        if (debug) {
          console.log("getSheetmeta for debug");
          await getSheetMeta(event.worksheetId).then((meta) => {
            if (meta) {
              document.getElementById("metaInput").value = JSON.stringify(meta);
              //console.log(data);
            }
          });
        }
      }).catch(function (error) {
        console.error(error);
      });
    } else {
      activatedByTreeSheets.delete(event.worksheetId);
      console.log("sheetActivatedHandler event ignored");
    }
  }, "Activating sheet");
}
*/

// A simple deep equality check using JSON (sufficient for many cases)
function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Compares two arrays of objects line-by-line.
 * Returns an object with:
 *  - outcome: a number (1-7) for the detected scenario,
 *  - message: a brief description,
 *  - differences: an array of objects { index, arr1, arr2 } for every differing line.
 *
 * Scenarios:
 * 1. Arrays are identical.
 * 2. Arrays identical until arr1 ends; arr2 has extra objects.
 * 3. Arrays identical until arr2 ends; arr1 has extra objects.
 * 4. Same length; every line differs.
 * 5. Same length; identical until a given index and then every following line differs.
 * 6. Same length; differences occur non-contiguously.
 * 7. Arrays have different lengths, and the common portion already contains differences.
 */
function compareArrays(arr1, arr2) {
  const differences = [];
  const maxLen = Math.max(arr1.length, arr2.length);

  // Compare every index up to the longest array length.
  for (let i = 0; i < maxLen; i++) {
    if (i < arr1.length && i < arr2.length) {
      if (!deepEqual(arr1[i], arr2[i])) {
        differences.push({ index: i, arr1: arr1[i], arr2: arr2[i] });
      }
    } else if (i >= arr1.length) {
      // arr1 ended; extra element in arr2.
      differences.push({ index: i, arr1: undefined, arr2: arr2[i] });
    } else if (i >= arr2.length) {
      // arr2 ended; extra element in arr1.
      differences.push({ index: i, arr1: arr1[i], arr2: undefined });
    }
  }

  let outcome;
  let message = "";

  // If no differences at all.
  if (differences.length === 0) {
    outcome = 1;
    message = "Arrays are identical.";
    return { outcome, message, differences };
  }

  // Arrays have different lengths.
  if (arr1.length !== arr2.length) {
    const commonLength = Math.min(arr1.length, arr2.length);
    let identicalCommon = true;
    for (let i = 0; i < commonLength; i++) {
      if (!deepEqual(arr1[i], arr2[i])) {
        identicalCommon = false;
        break;
      }
    }
    if (identicalCommon) {
      outcome = arr1.length < arr2.length ? 2 : 3;
      message =
        arr1.length < arr2.length
          ? "Arrays are identical until arr1 ends; arr2 has extra objects. MEANS: user added a sheet while TS was closed"
          : "Arrays are identical until arr2 ends; arr1 has extra objects. MEANS: user removed a sheet from the end while TS was closed";
    } else {
      outcome = 7;
      message =
        "Arrays differ in their overlapping part and have extra objects in one of the arrays (mixed differences).";
    }
    return { outcome, message, differences };
  }

  // Now, arr1 and arr2 have the same length.
  const firstDiff = differences[0].index;
  // Check if differences occur contiguously from the first difference onward.
  if (differences.length === arr1.length - firstDiff) {
    if (firstDiff === 0) {
      outcome = 4;
      message =
        "Arrays have the same length and differ in every line. MEANS: everythings completely jumbled. but that should be fixable, no?";
    } else {
      outcome = 5;
      message =
        "Arrays are identical until index " +
        firstDiff +
        ", then differ for every following line. MEANS, something got jumbled at the end. potentially fixable??";
    }
  } else {
    outcome = 6;
    message = "Arrays have the same length and differences are non-contiguous. MEANS: that's a mess.........";
  }

  return { outcome, message, differences };
}

export async function trackWorksheetPositions(type, index, end, tree) {
  //get, track, save
  return Excel.run(async (context) => {
    console.log("trackWorksheetPositions", type, index, end, tree);

    if (type == "get") {
      let data = getWorkbookMeta();
      $(data).each(function (index, item) {
        console.log(JSON.stringify(item));
      });

      if (data == null) {
        await trackWorksheetPositions("save");
      } else {

        let sheets = context.workbook.worksheets;
        sheets.load("items.id, items.position");
        await context.sync();
        console.log(sheets);

        let currentPositions = sheets.items.map((sheet) => ({ id: sheet.id, position: sheet.position }));
        if (JSON.stringify(data) != JSON.stringify(currentPositions)) {
          const res = compareArrays(
            data.map(({ id, position }) => ({ id, position })),
            currentPositions
          );
          console.log(res);
          if (res.outcome != 1) {
            await showMessage("blubb", "OK");
          }
        }
      }

      return data;
    } else if (type == "track") {
      return "trackWorksheetPositions track prevented by early return";
      //await getSheetMeta();
      // eslint-disable-next-line no-unreachable
      const sheets = context.workbook.worksheets;
      sheets.load("items.id, items.position");
      await context.sync();

      let currentPositions = sheets.items.map((sheet) => ({ id: sheet.id, position: sheet.position }));
      const meta = JSON.stringify(getWorkbookMeta());
      //console.log("meta", meta);
      if (meta == "null") {
        console.log("first launch");
        await trackWorksheetPositions("save", 4);
        return;
      }

      if (meta != JSON.stringify(currentPositions)) {
        console.log(JSON.parse(meta), currentPositions);
        const res = compareArrays(JSON.parse(meta), currentPositions);
        console.log(res);
        if (res.outcome != 1) {
          const response = await showMessage(
            res.outcome +
              " " +
              res.message +
              " A worksheet has been moved to a new position or opened via the Bottom Bar while Tree Sheets was closed. The offending sheets will be moved to the end.",
            "OK",
            ""
          );
          console.log("User response:", response);
        }
      }
    } else if (type == "save") {
      //FIXME weird bug here, should only kick in when moved manually..?
      if (!tree) {
        const selectedElements = $("#tree li").slice(index, end + 1);
        const levels = selectedElements
          .map(function () {
            return $(this).data("level");
          })
          .get();
        const allSameLevel = levels.every((level) => level === levels[0]);

        if (!allSameLevel) {
          await showMessage("Dun do dis, restoring previous order...", "Acknowledge", "");
          const id2 = $("#tree li").eq(end).attr("data-id");
          console.log("add to movedByTreeSheets", id2);
          movedByTreeSheets.add(id2);
          await applyWorksheetPositions(getWorkbookMeta());
          return;
        }
      }
      
      //Wokbookmeta
      const workbookMeta = getWorkbookMeta();

      //Sheet props. Set defaults if no data an certain porperties
      let sheets = context.workbook.worksheets;
      sheets.load("items.id, items.tabColor, items.visibility, items.name, items.position");
      await context.sync();
      const data = sheets.items.map((sheet) => ({
        id: sheet.id,
        name: sheet.name,
        position: sheet.position,
        tabColor: sheet.tabColor || "",
        visibility: sheet.visibility || "Visible",
        level: sheet.level || 1,
        parent_id: sheet.parent_id || "0",
      }));

      //Tree props
      let mods = [];
      $("li.tree-branch").each(function () {
        mods.push({
          id: $(this).data("id"),
          level: $(this).data("level"),
          parent_id: $(this).data("parent").toString(),
          position: $(this).index(),
        });
      });

      //Merge sheet props with tree props into one array of objects.
      data.forEach((item) => {
        const mod = mods.find((modItem) => modItem.id === item.id);
        if (mod) {
          item.level = mod.level;
          item.parent_id = mod.parent_id.toString();
          if (item.position !== mod.position) {
            item.position = tree ? mod.position : item.position;
          }
        }
      });

      //Merge workbook meta
      if (workbookMeta) {
        data.forEach((item) => {
          const mod = workbookMeta.find((modItem) => modItem.id === item.id);
          item.tabColor = mod ? mod.tabColor : item.tabColor;
        });
      }

      data.sort((a, b) => a.position - b.position);

      await saveWorkbookMeta(data);

      console.log("Data stored in Workbook:");
      $(data).each(function () {
        console.log(JSON.stringify(this));
      });
      if (!tree) {
        //console.log("reload sheets")
        //await loadSheets()
      }
    }
  });
}

export async function createWorksheet(name, meta, color) {
  console.log("createWorksheet", name, meta, color);
  return Excel.run(async (context) => {
    try {
      const sheet = context.workbook.worksheets.add();
      sheet.position = meta.position;
      sheet.load("id, name");
      //await context.sync();
      sheet.tabColor = color;
      await context.sync();
      return { id: sheet.id, name: sheet.name };
    } catch (error) {
      console.error(error);
      console.log("nope");
      return false;
    }
  });
}

let deletedByTreeSheets = new Set();
let addedByTreeSheets = new Set();
let activatedByTreeSheets = new Set();
let movedByTreeSheets = new Set();
let visibilityByTreeSheets = new Set();

window.deletedByTreeSheets = deletedByTreeSheets;
window.addedByTreeSheets = addedByTreeSheets;
window.activatedByTreeSheets = activatedByTreeSheets;
window.movedByTreeSheets = movedByTreeSheets;
window.visibilityByTreeSheets = visibilityByTreeSheets;

export async function deleteWorksheetById(sheetId) {
  console.log("deleteWorksheetById", sheetId);
  if ($("#tree > li").length > 1) {
    try {
      console.log("added to deletedByTreeSheets", sheetId);
      deletedByTreeSheets.add(sheetId);
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetId);
        console.log("deleting", sheetId);
        sheet.delete();
        await context.sync();
      });
      await trackWorksheetPositions("save", 5);
    } catch (error) {
      console.error(error);
    }
  }
}

export async function isWorksheetEmpty(sheetId) {
  console.log("isWorksheetEmpty", sheetId);
  try {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetId);
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load("values");
      await context.sync();
      if (usedRange.isNullObject || usedRange.values.flat().every((value) => value === "" || value === null)) {
        console.log("worksheet is empty");
        return true;
      } else {
        console.log("worksheet is not empty");
        return false;
      }
    });
  } catch (error) {
    console.error(error);
  }
}

export let messageResolver;

export function showMessage(message, btn1Text = "OK", btn2Text = "Cancel") {
  return new Promise((resolve) => {
    messageResolver = resolve;
    document.getElementById("modalMessage").innerHTML = message;
    document.getElementById("modalBtn1").innerText = btn1Text;
    if (btn2Text == "" || btn2Text == undefined) {
      $(document.getElementById("modalBtn2")).hide();
    }
    document.getElementById("modalBtn2").innerText = btn2Text;
    document.getElementById("messageModal").style.display = "flex";
  });
}

export function resolveMessage(response) {
  messageResolver(response);
  closeModal();
}

export function closeModal() {
  document.getElementById("messageModal").style.display = "none";
}
